import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../../utils/cn';

const base =
  'border inline-flex items-center justify-center gap-1.5 font-medium whitespace-nowrap rounded-lg ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-opacity-25 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-opacity duration-100 ease-in-out';
const variant = {
  dark: '!border-transparent bg-gray-950 text-white hover:!bg-gray-700 focus:!bg-gray-700 focus-visible:ring-gray-400 disabled:!bg-gray-400',
  secondary:
    'bg-white text-gray-950 !border-gray-300 hover:!bg-gray-100 hover:no-underline focus-visible:ring-gray-400 disabled:!bg-white disabled:!text-gray-400',
  primary_dark_light:
    '!border-transparent bg-gray-100 text-gray-950 hover:!bg-gray-200 disabled:bg-gray-400 focus-visible:ring-gray-400  disabled:!bg-gray-100 disabled:!text-gray-400',
  white: '!border-gray-200 text-gray-950 bg-white hover:!bg-gray-100 disabled:!text-gray-400',
  danger:
    'bg-red-500 !border-red-500 hover:bg-red-600 text-white disabled:!bg-red-300 disabled:!text-red-50 disabled:!border-red-50',
  success:
    '!border-transparent bg-green-500 text-white focus-visible:ring-green-400 hover:bg-green-600 disabled:!text-green-50 disabled:!bg-green-300',
  link: '!border-transparent text-gray-950 hover:opacity-55 hover:no-underline focus-visible:ring-gray-400 disabled:!text-gray-400',
  ghost: '!border-transparent hover:!bg-gray-100',
  custom: '',
};

const buttonVariants = cva(base, {
  variants: {
    variant,
    size: {
      icon: 'h-[34px] w-[34px] px-2 py-2 text-sm',
      xxs: 'h-[20px] px-3 py-2 text-xs rounded-md',
      xs: 'h-[26px] px-3 py-2 text-xs rounded-md',
      sm: 'h-[34px] px-3 py-2 text-sm',
      md: 'h-[41px] px-5 py-2.5 text-sm',
      lg: 'h-[48px] px-5 py-3 text-base',
      xl: 'h-[50px] px-6 py-3.5 text-base',
    },
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md',
  },
});

const iconButtonVariants = cva(base, {
  variants: {
    variant,
    size: {
      sm: 'h-[34px] h-[34px] p-2 text-sm',
      md: 'h-[41px] w-[41px] text-sm',
      lg: 'h-[48px] w-[48px] p-3 text-base',
      xl: 'h-[50px] w-[50px] p-3.5 text-base',
    },
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md',
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          'disabled:opacity-50 transition-opacity duration-300 ease-in-out',
        )}
        ref={ref}
        type="button"
        {...props}
      />
    );
  },
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          iconButtonVariants({ variant, size, className }),
          'disabled:opacity-50 transition-opacity duration-300 ease-in-out',
        )}
        ref={ref}
        type="button"
        {...props}
      />
    );
  },
);

export { Button, IconButton, buttonVariants, iconButtonVariants };
